
import { Vue, Component, Prop } from "vue-property-decorator";

import UPDATE_DEMO_REEL from "@/apollo/profile/demoReel/UpdateDemoReel.gql";
import GET_DEMO_REEL from "@/apollo/profile/demoReel/GetDemoReel.gql";

import FormGroup from "@/components/form/FormGroup.vue";
import UiButton from '@/components/button/ui-button.vue';
import FormWrapper from "@/components/form/FormWrapper.vue";

import { NOTIFICATION } from "@/entities/Events";
import Message from "@/data/notificationsMessages";

interface Observer extends Vue {
  reset: Function;
}

@Component({
  components: {
    FormGroup,
    UiButton,
    FormWrapper,
  },
  apollo: {
    demoReel: {
      query: GET_DEMO_REEL,
      variables() {
        return {
          id: this.userId,
        };
      },
      update (data) {
        const demoReel = data.user_profile_by_pk.demo_reel;
        this.initialDemoReelValue = demoReel;
        return demoReel;
      },
    },
  },
})
export default class ProfileDemoReel extends Vue {
  @Prop({ type: String, required: true }) readonly userId!: string;

  demoReel: string = "";
  initialDemoReelValue: string = "";
  updating: boolean = false;

  deleting: boolean = false;

  get fetching(): boolean {
    return this.$apollo.queries.demoReel.loading;
  }

  get demoReelUpdateNeeded(): boolean {
    return this.demoReelChanged;
  }

  get existingDemoReelUpdated(): boolean {
    return Boolean(this.initialDemoReelValue && this.demoReel && this.initialDemoReelValue !== this.demoReel);
  }

  get demoReelDeleted(): boolean {
    return Boolean(this.initialDemoReelValue && !this.demoReel);
  }

  get demoReelAdded(): boolean {
    return Boolean(!this.initialDemoReelValue && this.demoReel);
  }

  get demoReelChanged(): boolean {
    return this.existingDemoReelUpdated
      || this.demoReelDeleted
      || this.demoReelAdded;
  }

  get demoReelValuePresent(): boolean {
    return Boolean(this.demoReel);
  }

  async updateDemoReel(): Promise<void> {
    if (!this.demoReelUpdateNeeded) {
      this.handleWarningNotifications();
      return;
    }

    this.setUpdatePurpose();

    try {
      await this.update();
      const purpose = this.demoReel ? "updated" : "deleted";
      this.$eventBus.emit(
        NOTIFICATION,
        Message.successMessage(`Your demo reel had been successfully ${purpose}.`)
      );
      this.refetch();
    } catch (error) {
      this.$eventBus.emit(
        NOTIFICATION,
        Message.errorMessage(error.message)
      );
    } finally {
      this.updating = false;
      this.deleting = false;
    }
  }

  async update(): Promise<void> {
    await this.$apollo.mutate({
      mutation: UPDATE_DEMO_REEL,
      variables: {
        id: this.userId,
        demoReel: this.demoReel,
      },
    });
  }

  refetch(): void {
    this.$apollo.queries.demoReel.refetch();
    this.$emit("update");
  }

  removeDemoReel(): void {
    this.demoReel = "";
    (this.$refs.observer as Observer).reset();
    this.updateDemoReel();
  }


  handleWarningNotifications(): void {
    if (!this.demoReelValuePresent) {
      this.$eventBus.emit(
        NOTIFICATION,
        Message.infoMessage("Please, enter your demo reel before submitting"),
      );
      return;
    }

    if (!this.demoReelChanged) {
      this.$eventBus.emit(
        NOTIFICATION,
        Message.infoMessage("You haven't updated your demo reel"),
      );
      return;
    }
  }

  setUpdatePurpose(): void {
    if (this.demoReel) {
      this.updating = true;
    }
    if (this.initialDemoReelValue && !this.demoReel) {
      this.deleting = true;
    }
  }
}
